import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useContext } from 'react';
import { useApiClient, API_URL } from '../client';
import { Context } from '@/util-components/root/context';
import { getErrorMessage } from '../../utils/handle-error';
import {
  PaginationResponse,
  RecentPost,
  PlannedPost,
  MostUsedTemplate,
  GenerateCaptionRequestDto,
  GenerateCaptionResponseDto,
} from '../interfaces/posts.interface';
import { userProfileKeys } from './user-profile.requests';

export const useCreatePost = (
  options?: {
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
  }
) => {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();
  const { refetchUser } = useContext(Context);

  return useMutation({
    mutationFn: (formData: FormData) =>
      apiRequest<any>('posts', 'POST', formData, {}, {
        throwOnError: true,
        skipGlobalErrorHandler: true // We handle errors locally for transparency
      }),
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['posts'] }),
        queryClient.invalidateQueries({ queryKey: userProfileKeys.all }),
      ]);
      await refetchUser?.();
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useSchedulePost = (
  options?: {
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
  }
) => {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();
  const { refetchUser } = useContext(Context);

  return useMutation({
    mutationFn: (formData: FormData) =>
      apiRequest<any>('posts/schedule', 'POST', formData, {}, {
        throwOnError: true,
        skipGlobalErrorHandler: true
      }),
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['posts'] }),
        queryClient.invalidateQueries({ queryKey: userProfileKeys.all }),
      ]);
      await refetchUser?.();
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useGenerateCaption = (
  options?: {
    onSuccess?: (data: GenerateCaptionResponseDto) => void;
    onError?: (error: any) => void;
  }
) => {
  const { apiRequest } = useApiClient();

  return useMutation({
    mutationFn: (data: GenerateCaptionRequestDto) =>
      apiRequest<GenerateCaptionResponseDto>('posts/captions/generate', 'POST', data, {}, {
        throwOnError: true,
        skipGlobalErrorHandler: true,
      }),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export function usePostsGetRecentInsights(
  pagination: { page: number; limit: number; q?: string; type?: string; origin?: string; sort?: string; dir?: string },
  options?: Omit<UseQueryOptions<PaginationResponse<RecentPost>>, "queryKey" | "queryFn">
) {
  const { accessToken, setError, currentDepartment } = useContext(Context);

  return useQuery({
    queryKey: ["posts", "insights", "recent", pagination.page, pagination.limit, pagination.q, pagination.type, pagination.origin, pagination.sort, pagination.dir, currentDepartment],
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<PaginationResponse<RecentPost>> => {
      const headers: Record<string, string> = { Accept: "application/json" };
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      if (currentDepartment) headers["x-department"] = currentDepartment;

      let url = `${API_URL}/posts/insights/recent?page=${pagination.page}&limit=${pagination.limit}`;
      if (pagination.q) {
        url += `&q=${encodeURIComponent(pagination.q)}`;
      }
      if (pagination.type) {
        url += `&type=${pagination.type}`;
      }
      if (pagination.origin) {
        url += `&origin=${pagination.origin}`;
      }
      if (pagination.sort) {
        url += `&sort=${pagination.sort}`;
      }
      if (pagination.dir) {
        url += `&dir=${pagination.dir}`;
      }

      const response = await fetch(url, { method: "GET", headers });

      if (!response.ok) {
        const error = await getErrorMessage(response);
        setError?.(error);
        throw new Error(`Request failed: ${response.status}`);
      }

      return await response.json();
    },
    ...options,
  });
}

export function usePostsGetPlannedInsights(
  pagination: { page: number; limit: number; q?: string },
  options?: Omit<UseQueryOptions<PaginationResponse<PlannedPost>>, "queryKey" | "queryFn">
) {
  const { accessToken, setError, currentDepartment } = useContext(Context);

  return useQuery({
    queryKey: ["posts", "insights", "planned", pagination.page, pagination.limit, pagination.q, currentDepartment],
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<PaginationResponse<PlannedPost>> => {
      const headers: Record<string, string> = { Accept: "application/json" };
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      if (currentDepartment) headers["x-department"] = currentDepartment;

      let url = `${API_URL}/posts/insights/planned?page=${pagination.page}&limit=${pagination.limit}`;
      if (pagination.q) {
        url += `&q=${encodeURIComponent(pagination.q)}`;
      }

      const response = await fetch(url, { method: "GET", headers });

      if (!response.ok) {
        const error = await getErrorMessage(response);
        setError?.(error);
        throw new Error(`Request failed: ${response.status}`);
      }

      return await response.json();
    },
    ...options,
  });
}

export function usePostsGetPlannedPost(
  id: string | undefined,
  options?: Omit<UseQueryOptions<PlannedPost>, "queryKey" | "queryFn">
) {
  const { accessToken, setError, currentDepartment } = useContext(Context);

  return useQuery({
    queryKey: ["posts", "planned", id],
    enabled: !!id && (options?.enabled ?? true),
    queryFn: async (): Promise<PlannedPost> => {
      const headers: Record<string, string> = { Accept: "application/json" };
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      if (currentDepartment) headers["x-department"] = currentDepartment;

      const response = await fetch(`${API_URL}/posts/schedule/${id}`, { method: "GET", headers });

      if (!response.ok) {
        const error = await getErrorMessage(response);
        setError?.(error);
        throw new Error(`Request failed: ${response.status}`);
      }

      return await response.json();
    },
    ...options,
  });
}

export function usePostsGetTemplateInsights(
  pagination: { page: number; limit: number },
  options?: Omit<UseQueryOptions<PaginationResponse<MostUsedTemplate>>, "queryKey" | "queryFn">
) {
  const { accessToken, setError, currentDepartment } = useContext(Context);

  return useQuery({
    queryKey: ["posts", "insights", "templates", pagination.page, pagination.limit, currentDepartment],
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<PaginationResponse<MostUsedTemplate>> => {
      const headers: Record<string, string> = { Accept: "application/json" };
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      if (currentDepartment) headers["x-department"] = currentDepartment;

      const response = await fetch(`${API_URL}/posts/insights/templates?page=${pagination.page}&limit=${pagination.limit}`, { method: "GET", headers });

      if (!response.ok) {
        const error = await getErrorMessage(response);
        setError?.(error);
        throw new Error(`Request failed: ${response.status}`);
      }

      return await response.json();
    },
    ...options,
  });
}

export const usePostsDeletePlanned = (
  options?: {
    onSuccess?: (data: void) => void;
    onError?: (error: any) => void;
  }
) => {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`posts/schedule/${id}`, 'DELETE', undefined, {}, {
        throwOnError: true,
      }),
    onSuccess: (data, variables) => {
      options?.onSuccess?.(data);
      queryClient.invalidateQueries({ queryKey: ["posts", "insights", "planned"] });
      queryClient.invalidateQueries({ queryKey: ["posts", "planned", variables] });
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const usePostsBulkDeletePlanned = (
  options?: {
    onSuccess?: (data: void) => void;
    onError?: (error: any) => void;
  }
) => {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) =>
      apiRequest<void>(`posts/schedule/bulk-delete`, 'POST', { ids }, {}, {
        throwOnError: true,
      }),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
      queryClient.invalidateQueries({ queryKey: ["posts", "insights", "planned"] });
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const usePostsReschedulePlanned = (
  options?: {
    onSuccess?: (data: PlannedPost) => void;
    onError?: (error: any) => void;
  }
) => {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, scheduledFor }: { id: string; scheduledFor: string }) =>
      apiRequest<PlannedPost>(`posts/schedule/${id}`, 'PATCH', { scheduledFor }, {}, {
        throwOnError: true,
      }),
    onSuccess: (data, variables) => {
      options?.onSuccess?.(data);
      queryClient.invalidateQueries({ queryKey: ["posts", "insights", "planned"] });
      queryClient.invalidateQueries({ queryKey: ["posts", "planned", variables.id] });
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export function usePostsGetPost(
  id: string | undefined,
  options?: Omit<UseQueryOptions<RecentPost>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: ["posts", id],
    enabled: !!id && (options?.enabled ?? true),
    queryFn: () => apiRequest<RecentPost>(`posts/${id}`, 'GET'),
    ...options,
  });
}
